/* eslint-disable array-callback-return,consistent-return,no-param-reassign */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import uniqBy from "lodash/uniqBy";
import {
  postPropertiesRoute,
  getPropertiesRoute,
  patchPropertyRoute,
  getPropertiesVersionsRoute,
  deletePropertyRoute,
  getActionHistoryRoute,
} from "../../lib/routes/properties";
import Message from "../../components/Message/Message";
import { toDisplayFormat } from "../../lib/utils/formatDate";
import { getUserByIdRoute } from "../../lib/routes/security";
import { Property, Action } from "../../lib/interfaces";
import { LoadingStatusCode, ObjectTypeSysname } from "../../lib/types";
import { catchApiError } from "../../lib/utils/catchApiError";

interface InitialState {
  properties: Property[];
  updatePropertyData?: Property;
  propertiesVersions: Property[];
  createPropertyData?: Property;
  billActionHistory: Action[];
  uniqUsers: Action[];
  propertiesLoading: LoadingStatusCode;
  createPropertyDataLoading: LoadingStatusCode;
  updatePropertyDataLoading: LoadingStatusCode;
  propertiesVersionsLoading: LoadingStatusCode;
  deletePropertiesLoading: LoadingStatusCode;
  billActionHistoryLoading: LoadingStatusCode;
}

const initialState: InitialState = {
  properties: [],
  propertiesVersions: [],
  billActionHistory: [],
  uniqUsers: [],
  propertiesLoading: "idle",
  createPropertyDataLoading: "idle",
  updatePropertyDataLoading: "idle",
  propertiesVersionsLoading: "idle",
  deletePropertiesLoading: "idle",
  billActionHistoryLoading: "idle",
};

const fetchBillActionHistory = createAsyncThunk<
  Action[],
  { objectTypeSysname: ObjectTypeSysname; objectId: number }
>("properties/fetchBillActionHistory", async ({ objectTypeSysname, objectId }) => {
  const url = getActionHistoryRoute(objectTypeSysname, objectId);
  const { data } = await axios.get<Action[]>(url);
  let count = 0;
  const dataLogin = await Promise.all(
    data.map(async (item) => {
      try {
        const arrUniqUsers = uniqBy(data, "userId");
        const check = arrUniqUsers.find((checkItem) => checkItem.id === item.id);
        if (check) {
          const response = await axios.get(getUserByIdRoute(item.userId));
          item.login = response?.data?.login;
        }
      } catch (e) {
        count++;
        if (count === 1) {
          return catchApiError({
            e,
            title: "",
          });
        }
      }
      return item;
    })
  );
  return dataLogin;
});

const createProperty = createAsyncThunk<
  Property,
  { objectTypeSysname: ObjectTypeSysname; objectId: number; values: Partial<Property> }
>("properties/createProperty", async ({ objectTypeSysname, objectId, values }) => {
  try {
    const response = axios.post<Property>(postPropertiesRoute(objectTypeSysname, objectId), values);
    const { data } = await response;
    const { status } = await response;
    Message("Свойство успешно создано", status);
    return data;
  } catch (e: any) {
    if (e?.response?.data.message.includes("Property с типом")) {
      return catchApiError({
        e,
        title: `Ошибка создания свойства. Данное свойство уже существует на ${toDisplayFormat(
          values?.fromDate
        )}, выберите другую дату`,
      });
    }
    return catchApiError({
      e,
      title: "Ошибка создания свойства",
    });
  }
});

const fetchProperties = createAsyncThunk<
  Property[],
  { objectTypeSysname: ObjectTypeSysname; objectId: number; includeClosed: boolean }
>("properties/fetchProperties", async (props) => {
  try {
    const url = getPropertiesRoute(props);
    const { data } = await axios.get<Property[]>(url);
    data.sort((a, b) => +new Date(b.fromDate) - +new Date(a.fromDate));
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки свойств. Попробуйте позже",
    });
  }
});

const fetchPropertiesVersions = createAsyncThunk<Property[], { id: number }>(
  "properties/fetchPropertiesVersions",
  async ({ id }) => {
    try {
      const url = getPropertiesVersionsRoute(id);
      const { data } = await axios.get<Property[]>(url);
      data.sort((a, b) => +new Date(b.toDate) - +new Date(a.toDate));
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки свойств. Попробуйте позже",
      });
    }
  }
);

const updateProperty = createAsyncThunk<
  Property,
  { id: number; isVersionUpdated: boolean; fromDate: string; values: Partial<Property> }
>("properties/updateProperty", async ({ id, isVersionUpdated, fromDate, values }) => {
  try {
    const url = patchPropertyRoute({ id, force: isVersionUpdated, fromDate });
    const response = axios.patch<Property>(url, values);
    const { data } = await response;
    const { status } = await response;
    if (!isVersionUpdated) Message("Версия успешно создана", status);
    else Message("Версия успешно обновлена", status);

    return data;
  } catch (e) {
    if (!isVersionUpdated) {
      return catchApiError({
        e,
        title: "Ошибка создания версии",
      });
    }
    return catchApiError({
      e,
      title: "Ошибка обновления версии",
    });
  }
});

const deleteProperties = createAsyncThunk<Promise<any>, { id: number; fromDate: string }>(
  "properties/deleteProperties",
  async ({ id, fromDate }) => {
    try {
      const url = deletePropertyRoute(id, fromDate);
      const { data, status } = await axios.delete(url);
      Message("Дата закрытия успешно изменена", status);
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка закрытия версии",
      });
    }
  }
);

const slice = createSlice({
  name: "properties",
  initialState,
  reducers: {
    cleanBillActionHistory: (state) => {
      state.billActionHistory = [];
      state.billActionHistoryLoading = "idle";
    },
    cleanProperties: (state) => {
      state.properties = [];
      state.propertiesLoading = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBillActionHistory.fulfilled, (state, action) => {
        state.billActionHistory = action.payload;
        state.uniqUsers = uniqBy(action.payload, "userId");
        state.billActionHistoryLoading = "fulfilled";
      })
      .addCase(fetchBillActionHistory.pending, (state) => {
        state.billActionHistoryLoading = "pending";
      })
      .addCase(fetchBillActionHistory.rejected, (state) => {
        state.billActionHistoryLoading = "rejected";
      })
      .addCase(fetchProperties.fulfilled, (state, { payload }) => {
        state.properties = payload;
        state.propertiesLoading = "fulfilled";
      })
      .addCase(fetchProperties.pending, (state) => {
        state.propertiesLoading = "pending";
      })
      .addCase(fetchProperties.rejected, (state) => {
        state.propertiesLoading = "rejected";
      })
      .addCase(createProperty.pending, (state) => {
        state.createPropertyDataLoading = "pending";
      })
      .addCase(createProperty.fulfilled, (state, action) => {
        state.createPropertyData = action.payload;
        state.createPropertyDataLoading = "fulfilled";
      })
      .addCase(createProperty.rejected, (state) => {
        state.createPropertyDataLoading = "rejected";
      })
      .addCase(updateProperty.pending, (state) => {
        state.updatePropertyDataLoading = "pending";
      })
      .addCase(updateProperty.fulfilled, (state, action) => {
        state.updatePropertyData = action.payload;
        state.updatePropertyDataLoading = "fulfilled";
      })
      .addCase(updateProperty.rejected, (state) => {
        state.updatePropertyDataLoading = "rejected";
      })
      .addCase(fetchPropertiesVersions.fulfilled, (state, { payload }) => {
        state.propertiesVersions = payload;
        state.propertiesVersionsLoading = "fulfilled";
      })
      .addCase(fetchPropertiesVersions.pending, (state) => {
        state.propertiesVersionsLoading = "pending";
      })
      .addCase(fetchPropertiesVersions.rejected, (state) => {
        state.propertiesVersionsLoading = "rejected";
      })

      .addCase(deleteProperties.fulfilled, (state) => {
        state.deletePropertiesLoading = "fulfilled";
      })
      .addCase(deleteProperties.pending, (state) => {
        state.deletePropertiesLoading = "pending";
      })
      .addCase(deleteProperties.rejected, (state) => {
        state.deletePropertiesLoading = "rejected";
      });
  },
});

const { actions, reducer } = slice;
export {
  actions as propertiesActions,
  fetchBillActionHistory,
  fetchProperties,
  createProperty,
  updateProperty,
  fetchPropertiesVersions,
  deleteProperties,
};
export default reducer;
